import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FileText, X, ExternalLink, Download, Printer } from 'lucide-react';

function fmt(d) { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); } catch { return '—'; } }
function fmtCur(n) { return '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 }); }
function isValidPdfUrl(url) { return typeof url === 'string' && url.startsWith('http') && !url.includes('[object') && url.length > 10; }

const TYPE_LABELS = { sale:'Tax Invoice', delivery:'Delivery Challan' };
const TYPE_COLORS = { sale:'bg-green-100 text-green-700', delivery:'bg-blue-100 text-blue-700' };

export default function ClientSalesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [viewId, setViewId]     = useState(null);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    api.get('/client/sales')
      .then(r => setInvoices(r.data || []))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const openInvoice = async (id) => {
    setViewId(id); setViewLoading(true);
    try { const r = await api.get(`/client/sales/${id}`); setViewData(r.data); }
    catch { toast.error('Failed to load invoice details'); }
    finally { setViewLoading(false); }
  };

  const openPdf = async (url) => {
    if (!isValidPdfUrl(url)) { toast.error('PDF not available or was not uploaded correctly. Please ask admin to re-upload.'); return; }
    try { const res = await fetch(url); const blob = await res.blob(); window.open(URL.createObjectURL(new Blob([blob],{type:'application/pdf'})),'_blank'); }
    catch { toast.error('Could not open PDF.'); }
  };

  const downloadPdf = async (url, filename) => {
    if (!isValidPdfUrl(url)) { toast.error('PDF not available or was not uploaded correctly. Please ask admin to re-upload.'); return; }
    try {
      const res = await fetch(url); const blob = await res.blob();
      const a = document.createElement('a'); a.href=URL.createObjectURL(new Blob([blob],{type:'application/pdf'})); a.download=filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { toast.error('Download failed.'); }
  };

  // ── Print formatted invoice ───────────────────────────────────────────────
  const handlePrint = () => {
    if (!viewData) return;
    const d = viewData;
    const subtotal  = parseFloat(d.subtotal||0);
    const discAmt   = parseFloat(d.discount_amount||0);
    const taxable   = subtotal - discAmt;
    const cgst      = parseFloat(d.cgst||0);
    const sgst      = parseFloat(d.sgst||0);
    const igst      = parseFloat(d.igst||0);
    const total     = parseFloat(d.total_amount||0);
    const isIgst    = igst > 0;

    const itemRows = (d.items||[]).map((it,i) => {
      const lineTotal = (parseFloat(it.quantity)||0)*(parseFloat(it.rate)||0);
      const taxRate   = parseFloat(it.tax_rate||0);
      const lineGST   = lineTotal * taxRate / 100;
      return `
        <tr style="border-bottom:1px solid #f1f5f9;background:${i%2===0?'white':'#f8fafc'}">
          <td style="padding:7px 8px;font-size:11px;color:#64748b">${i+1}</td>
          <td style="padding:7px 8px">
            <div style="font-size:12px;font-weight:600">${it.item_name||''}</div>
            ${it.description?`<div style="font-size:10px;color:#64748b">${it.description}</div>`:''}
          </td>
          <td style="padding:7px 8px;font-size:12px;text-align:center">${it.quantity||''}</td>
          <td style="padding:7px 8px;font-size:11px;text-align:center;color:#64748b">${it.unit||'pcs'}</td>
          <td style="padding:7px 8px;font-size:12px;text-align:right">${fmtCur(it.rate)}</td>
          ${taxRate>0?`<td style="padding:7px 8px;font-size:11px;text-align:center">${taxRate}%</td>`:'<td></td>'}
          <td style="padding:7px 8px;font-size:12px;text-align:right;font-weight:600">${fmtCur(lineTotal)}</td>
        </tr>`;
    }).join('');

    const html = `
      <html><head><title>${d.challan_number}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;font-size:12px;color:#1a1a1a}
        @media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
      </style></head><body>
      <div style="max-width:720px;margin:0 auto;padding:24px">

        <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #7c3aed;padding-bottom:14px;margin-bottom:18px">
          <div>
            <div style="font-size:20px;font-weight:800;color:#7c3aed">${d.company_name||'MOZI INNOVATORS LLP'}</div>
            ${d.company_address?`<div style="font-size:11px;color:#555;margin-top:3px">${d.company_address}</div>`:''}
            ${d.company_phone?`<div style="font-size:11px;color:#555">📞 ${d.company_phone}</div>`:''}
            ${d.company_gstin?`<div style="font-size:11px;color:#555">GSTIN: ${d.company_gstin}</div>`:''}
          </div>
          <div style="text-align:right">
            <div style="font-size:16px;font-weight:700;color:#7c3aed;letter-spacing:1px">TAX INVOICE</div>
            <div style="font-size:14px;font-weight:600;margin-top:2px">${d.challan_number}</div>
            <div style="font-size:11px;margin-top:4px;color:#6b7280">Date: ${fmt(d.challan_date)}</div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:18px">
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px">
            <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#64748b;margin-bottom:5px">Bill To</div>
            <div style="font-size:13px;font-weight:700">${d.client_name||''}</div>
            ${d.client_phone?`<div style="font-size:11px;color:#475569;margin-top:2px">📞 ${d.client_phone}</div>`:''}
            ${d.client_address?`<div style="font-size:11px;color:#475569;margin-top:2px">${d.client_address}</div>`:''}
            ${d.client_gstin?`<div style="font-size:11px;color:#475569;margin-top:2px">GSTIN: ${d.client_gstin}</div>`:''}
          </div>
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px">
            <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#64748b;margin-bottom:5px">Invoice Details</div>
            ${d.project_name?`<div style="font-size:11px;margin-bottom:2px"><b>Project:</b> ${d.project_name}</div>`:''}
            <div style="font-size:11px;margin-bottom:2px"><b>Invoice No:</b> ${d.challan_number}</div>
            <div style="font-size:11px"><b>Date:</b> ${fmt(d.challan_date)}</div>
            ${d.transport_name?`<div style="font-size:11px;margin-top:2px"><b>Transport:</b> ${d.transport_name}</div>`:''}
            ${d.vehicle_number?`<div style="font-size:11px;margin-top:2px"><b>Vehicle No:</b> ${d.vehicle_number}</div>`:''}
            ${d.vehicle_length?`<div style="font-size:11px;margin-top:2px"><b>Vehicle Length:</b> ${d.vehicle_length}</div>`:''}
          </div>
        </div>

        <table style="width:100%;border-collapse:collapse;margin-bottom:14px">
          <thead>
            <tr style="background:#7c3aed;color:white">
              <th style="padding:8px;text-align:left;font-size:11px">#</th>
              <th style="padding:8px;text-align:left;font-size:11px">Item Description</th>
              <th style="padding:8px;text-align:center;font-size:11px">Qty</th>
              <th style="padding:8px;text-align:center;font-size:11px">Unit</th>
              <th style="padding:8px;text-align:right;font-size:11px">Rate</th>
              <th style="padding:8px;text-align:center;font-size:11px">GST%</th>
              <th style="padding:8px;text-align:right;font-size:11px">Amount</th>
            </tr>
          </thead>
          <tbody>${itemRows}</tbody>
        </table>

        <div style="display:flex;justify-content:flex-end;margin-bottom:18px">
          <div style="min-width:250px">
            <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;color:#374151;border-bottom:1px solid #f1f5f9">
              <span>Subtotal (Rate × Qty)</span><span>${fmtCur(subtotal)}</span>
            </div>
            ${discAmt>0?`<div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;color:#ef4444;border-bottom:1px solid #f1f5f9"><span>Discount</span><span>— ${fmtCur(discAmt)}</span></div>`:''}
            <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;font-weight:600;color:#1e293b;border-bottom:1px solid #e2e8f0">
              <span>Taxable Value</span><span>${fmtCur(taxable)}</span>
            </div>
            ${!isIgst&&cgst>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px;color:#374151"><span>CGST</span><span>${fmtCur(cgst)}</span></div>`:''}
            ${!isIgst&&sgst>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px;color:#374151;border-bottom:1px solid #f1f5f9"><span>SGST</span><span>${fmtCur(sgst)}</span></div>`:''}
            ${isIgst&&igst>0?`<div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px;color:#374151;border-bottom:1px solid #f1f5f9"><span>IGST</span><span>${fmtCur(igst)}</span></div>`:''}
            <div style="display:flex;justify-content:space-between;padding:8px 0 4px;font-size:15px;font-weight:700;color:#7c3aed;border-top:2px solid #7c3aed;margin-top:4px">
              <span>TOTAL</span><span>${fmtCur(total)}</span>
            </div>
          </div>
        </div>

        ${d.notes?`<div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:6px;padding:10px 14px;margin-bottom:18px;font-size:11px;color:#5b21b6"><b>Notes:</b> ${d.notes}</div>`:''}

        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;margin-top:32px;padding-top:14px;border-top:1px solid #e2e8f0">
          ${['Prepared By','Authorised Signatory',"Receiver's Signature"].map(l=>`<div style="text-align:center"><div style="border-top:1px solid #94a3b8;margin-top:40px;padding-top:6px;font-size:10px;color:#64748b">${l}</div></div>`).join('')}
        </div>
      </div>
      </body></html>`;

    const win = window.open('','_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-black text-gray-900">My Invoices</h1>
        <p className="text-gray-400 text-sm mt-0.5">Invoices shared by your vendor</p>
      </div>

      {loading ? (
        <div className="card flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"/>
        </div>
      ) : invoices.length === 0 ? (
        <div className="card py-16 text-center">
          <FileText className="w-10 h-10 text-gray-200 mx-auto mb-3"/>
          <p className="text-gray-400 font-semibold">No invoices shared yet</p>
          <p className="text-gray-300 text-sm mt-1">Invoices will appear here once your vendor shares them</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead><tr>
                <th className="text-left">Invoice #</th>
                <th className="text-left">Project</th>
                <th className="text-left">Type</th>
                <th className="text-left">Date</th>
                <th className="text-right">Amount</th>
                <th className="text-center">Action</th>
              </tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}>
                    <td className="font-mono font-bold text-purple-700 cursor-pointer" onClick={() => openInvoice(inv.id)}>{inv.challan_number}</td>
                    <td onClick={() => openInvoice(inv.id)} className="cursor-pointer">
                      <p className="text-sm font-semibold text-gray-800">{inv.project_name || '—'}</p>
                      {inv.project_code && <p className="text-xs text-gray-400">{inv.project_code}</p>}
                    </td>
                    <td onClick={() => openInvoice(inv.id)} className="cursor-pointer">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLORS[inv.challan_type]||'bg-gray-100 text-gray-600'}`}>
                        {TYPE_LABELS[inv.challan_type]||inv.challan_type}
                      </span>
                    </td>
                    <td onClick={() => openInvoice(inv.id)} className="text-gray-500 cursor-pointer">{fmt(inv.challan_date)}</td>
                    <td onClick={() => openInvoice(inv.id)} className="text-right font-semibold text-gray-900 cursor-pointer">{fmtCur(inv.total_amount)}</td>
                    <td className="text-center">
                      {inv.send_type === 'pdf' && inv.pdf_url ? (
                        <button onClick={() => downloadPdf(inv.pdf_url, `INV-${inv.challan_number}.pdf`)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                          <Download className="w-3.5 h-3.5"/> Download PDF
                        </button>
                      ) : (
                        <button onClick={() => openInvoice(inv.id)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700">
                          <ExternalLink className="w-3.5 h-3.5"/> View Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {viewId && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:700}}>
              <div className="modal-header">
                <div>
                  <h2>Invoice Details</h2>
                  {viewData && <p>{TYPE_LABELS[viewData.challan_type]||viewData.challan_type} — {viewData.challan_number}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {viewData && viewData.send_type !== 'pdf' && (
                    <button onClick={handlePrint}
                      className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors">
                      <Printer size={14}/> Print Invoice
                    </button>
                  )}
                  <button className="modal-close" onClick={()=>{setViewId(null);setViewData(null);}}><X size={16}/></button>
                </div>
              </div>

              {viewLoading ? (
                <div className="modal-body flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"/>
                </div>
              ) : viewData && (
                <div className="modal-body space-y-5">
                  {/* Project banner */}
                  {viewData.project_name && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText size={14} className="text-white"/>
                      </div>
                      <div>
                        <p className="text-xs text-amber-600 font-semibold">Project</p>
                        <p className="text-sm font-bold text-amber-900">{viewData.project_name}</p>
                      </div>
                    </div>
                  )}

                  {/* Summary cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      ['Invoice #', viewData.challan_number],
                      ['Date', fmt(viewData.challan_date)],
                      ['Subtotal', fmtCur(viewData.subtotal)],
                      ...(parseFloat(viewData.discount_amount)>0?[['Discount', `— ${fmtCur(viewData.discount_amount)}`]]:[]),
                      ['Taxable Value', fmtCur((parseFloat(viewData.subtotal)||0)-(parseFloat(viewData.discount_amount)||0))],
                      ['Total Amount', fmtCur(viewData.total_amount)],
                    ].map(([k,v])=>(
                      <div key={k} className={`rounded-xl p-3 ${k==='Total Amount'?'bg-purple-50 border border-purple-100':'bg-gray-50'}`}>
                        <p className="text-xs text-gray-400 mb-0.5">{k}</p>
                        <p className={`text-sm font-semibold ${k==='Total Amount'?'text-purple-700':'text-gray-900'}`}>{v}</p>
                      </div>
                    ))}
                  </div>

                  {/* Tax breakdown */}
                  {(parseFloat(viewData.cgst)>0 || parseFloat(viewData.sgst)>0 || parseFloat(viewData.igst)>0) && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                      <p className="text-xs font-bold text-blue-700 mb-2 uppercase">Tax Breakdown</p>
                      <div className="grid grid-cols-3 gap-3">
                        {parseFloat(viewData.cgst)>0 && <div><p className="text-xs text-gray-400">CGST</p><p className="text-sm font-semibold">{fmtCur(viewData.cgst)}</p></div>}
                        {parseFloat(viewData.sgst)>0 && <div><p className="text-xs text-gray-400">SGST</p><p className="text-sm font-semibold">{fmtCur(viewData.sgst)}</p></div>}
                        {parseFloat(viewData.igst)>0 && <div><p className="text-xs text-gray-400">IGST</p><p className="text-sm font-semibold">{fmtCur(viewData.igst)}</p></div>}
                      </div>
                    </div>
                  )}

                  {/* Items table */}
                  {viewData.items?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 mb-2">Items</h3>
                      <div className="border border-gray-100 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-xs font-semibold text-gray-400 uppercase">
                            <tr>
                              <th className="text-left px-3 py-2">Item</th>
                              <th className="text-center px-2 py-2">Qty</th>
                              <th className="text-right px-2 py-2">Rate</th>
                              <th className="text-center px-2 py-2">GST%</th>
                              <th className="text-right px-3 py-2">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewData.items.map((it,i) => {
                              const lineTotal = (parseFloat(it.quantity)||0)*(parseFloat(it.rate)||0);
                              return (
                                <tr key={i} className={`border-t border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/50'}`}>
                                  <td className="px-3 py-2">
                                    <p className="font-semibold text-gray-900">{it.item_name}</p>
                                    {it.description && <p className="text-xs text-gray-400">{it.description}</p>}
                                  </td>
                                  <td className="text-center px-2 py-2 text-gray-600">{it.quantity} {it.unit}</td>
                                  <td className="text-right px-2 py-2 text-gray-600">{fmtCur(it.rate)}</td>
                                  <td className="text-center px-2 py-2 text-blue-600 font-medium">{it.tax_rate||0}%</td>
                                  <td className="text-right px-3 py-2 font-semibold">{fmtCur(lineTotal)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {viewData.notes && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{viewData.notes}</p>
                    </div>
                  )}

                  {/* PDF download if available */}
                  {viewData.pdf_url && (
                    <div className="flex items-center gap-3 bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                      <Download className="w-6 h-6 text-indigo-600 flex-shrink-0"/>
                      <div className="flex-1"><p className="text-sm font-semibold text-gray-900">Invoice PDF</p><p className="text-xs text-gray-400">Download your invoice PDF</p></div>
                      <div className="flex gap-2">
                        <button onClick={()=>openPdf(viewData.pdf_url)} className="btn-primary" style={{fontSize:12,padding:'7px 14px'}}><ExternalLink size={13}/> Open</button>
                        <button onClick={()=>downloadPdf(viewData.pdf_url,`INV-${viewData.challan_number}.pdf`)} className="btn-secondary" style={{fontSize:12,padding:'7px 14px'}}><Download size={13}/> Save</button>
                      </div>
                    </div>
                  )}

                  {/* Print prompt for system type */}
                  {viewData.send_type !== 'pdf' && !viewData.pdf_url && (
                    <div className="flex items-center gap-3 bg-purple-50 rounded-xl p-4 border border-purple-100">
                      <Printer className="w-6 h-6 text-purple-600 flex-shrink-0"/>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">Print / Save Invoice</p>
                        <p className="text-xs text-gray-400">Print this invoice or save as PDF from your browser</p>
                      </div>
                      <button onClick={handlePrint}
                        className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5">
                        <Printer size={14}/> Print
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
